import React, { createContext, useState, useCallback, useContext, useRef } from "react";

const Severity = {
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
};

// Alerts stack instead of replacing each other, so a quick follow-up toast
// never hides the one before it. Older ones drop off past this limit.
const MAX_VISIBLE_ALERTS = 4;

const AlertContext = createContext();

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id) => {
    setAlerts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const alert = useCallback(
    ({ title, detail, severity = Severity.INFO, duration = 5000, action = null }) => {
      const id = nextId.current++;
      setAlerts((prev) =>
        [...prev, { id, title, detail, severity, duration, action }].slice(-MAX_VISIBLE_ALERTS)
      );
    },
    []
  );

  // Closes the newest alert (kept for callers of the original single-alert API).
  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlerts((prev) => prev.slice(0, -1));
  };

  const latest = alerts[alerts.length - 1];

  return (
    <AlertContext.Provider
      value={{
        open: alerts.length > 0,
        title: latest?.title ?? "",
        detail: latest?.detail ?? "",
        severity: latest?.severity ?? Severity.INFO,
        duration: latest?.duration ?? 5000,
        alerts,
        alert,
        dismiss,
        handleClose,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};
